import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaBook, FaShoppingCart, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { API_BASE_URL } from '../utils/constants';

function CourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPurchased, setIsPurchased] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    loadCourse();
    if (user) {
      checkPurchaseStatus();
    }
  }, [id, user]);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar when scrolled past the hero image (approximately 200px)
      setShowStickyBar(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const courseData = await apiService.getCourse(id);
      setCourse(courseData);
      setError(null);
    } catch (err) {
      setError('فشل تحميل تفاصيل الكتاب');
      console.error('Error loading course:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const purchases = await apiService.getUserPurchases();
      // Check if the course is purchased and paid
      const purchased = purchases.some(p => p.id === parseInt(id) && p.payment_status === 'paid');
      setIsPurchased(purchased);
    } catch (err) {
      console.error('Error checking purchase status:', err);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/course/${id}` } });
      return;
    }

    setPurchasing(true);
    try {
      // Create Chargily checkout and redirect to payment page
      const { checkoutUrl } = await apiService.createCheckout(parseInt(id));
      
      // Redirect to Chargily payment page
      window.location.href = checkoutUrl;
    } catch (err) {
      alert(err.message || 'فشلت عملية إنشاء الدفع. يرجى المحاولة لاحقاً.');
      console.error('Checkout error:', err);
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-bg to-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
          <p className="mt-4 text-gray-600 text-lg">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-bg to-white">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{error || 'الكتاب غير موجود'}</h2>
          <Link to="/courses" className="text-primary hover:underline">
            العودة إلى المكتبة
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen bg-gradient-to-b from-bg to-white py-20 overflow-x-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-[1200px] mx-auto">
          <Link to="/courses" className="inline-flex items-center gap-2 text-primary hover:underline mb-8">
            <FaArrowRight />
            العودة إلى المكتبة
          </Link>

          <div className="bg-white rounded-3xl shadow-custom-lg overflow-hidden">
              {course.summary_image_url && (
                <div className="w-full relative">
                  <img 
                    src={`${API_BASE_URL}/api/courses/${course.id}/thumbnail`}
                    alt={course.title_ar}
                    className="w-full h-auto object-cover block"
                    onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                  />
                  {/* Branding banner overlay */}
                  <div className="absolute bottom-0 left-0 right-0" style={{
                    background: 'linear-gradient(to right, rgba(0,52,128,0.88), rgba(0,82,204,0.88), rgba(0,52,128,0.88))',
                  }}>
                    <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-1 sm:py-1.5 md:py-2">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <img src="/icon.png" alt="Allemni" className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 rounded" />
                        <span className="text-white font-bold text-[10px] sm:text-xs md:text-sm">Allemni</span>
                        <span className="text-white/60 text-[8px] sm:text-[9px] md:text-xs">علمني</span>
                      </div>
                      <span className="text-white/50 text-[7px] sm:text-[8px] md:text-[10px]">allemni.site</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-3 mb-4">
                  <i className={`${course.category_icon} text-primary text-2xl`}></i>
                  <span className="text-gray-600 font-bold">{course.category_name}</span>
                </div>

                <h1 className="text-4xl font-black text-gray-800 mb-6">
                  {course.title_ar}
                </h1>

                {/* Available Content Types - Always Show */}
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-6 md:p-8 mb-8">
                  <h3 className="text-2xl font-black text-gray-900 mb-2 flex items-center gap-3">
                    <i className="fas fa-box-open text-primary"></i>
                    محتويات الدورة المتاحة
                  </h3>
                  <p className="text-gray-600 mb-6">الدورة تحتوي على 5 أنواع من المحتوى التعليمي</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Video Explainer */}
                    <div className="bg-white rounded-xl p-5 border-2 border-red-200 hover:border-red-400 transition-all hover:shadow-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <i className="fas fa-play text-white text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-black text-gray-900 text-lg mb-1">فيديو توضيحي</h4>
                          <p className="text-gray-600 text-sm">مقدمة تمهيدية للدخول للمحتوى بشكل مثالي</p>
                        </div>
                        <i className="fas fa-check-circle text-green-500 text-xl"></i>
                      </div>
                    </div>

                    {/* Written Content */}
                    <div className="bg-white rounded-xl p-5 border-2 border-blue-200 hover:border-blue-400 transition-all hover:shadow-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <i className="fas fa-book-open text-white text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-black text-gray-900 text-lg mb-1">محتوى مكتوب</h4>
                          <p className="text-gray-600 text-sm">محتوى شامل مع أمثلة عملية وتطبيقات واقعية</p>
                        </div>
                        <i className="fas fa-check-circle text-green-500 text-xl"></i>
                      </div>
                    </div>

                    {/* Presentation */}
                    <div className="bg-white rounded-xl p-5 border-2 border-orange-200 hover:border-orange-400 transition-all hover:shadow-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <i className="fas fa-presentation text-white text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-black text-gray-900 text-lg mb-1">عرض تقديمي</h4>
                          <p className="text-gray-600 text-sm">شرائح مرئية منظمة لتسهيل الفهم والاستيعاب</p>
                        </div>
                        <i className="fas fa-check-circle text-green-500 text-xl"></i>
                      </div>
                    </div>

                    {/* Flashcards */}
                    <div className="bg-white rounded-xl p-5 border-2 border-purple-200 hover:border-purple-400 transition-all hover:shadow-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <i className="fas fa-layer-group text-white text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-black text-gray-900 text-lg mb-1">بطاقات تعليمية</h4>
                          <p className="text-gray-600 text-sm">ملخصات سريعة ونقاط أساسية للمراجعة</p>
                        </div>
                        <i className="fas fa-check-circle text-green-500 text-xl"></i>
                      </div>
                    </div>

                    {/* Visual Summary */}
                    <div className="bg-white rounded-xl p-5 border-2 border-green-200 hover:border-green-400 transition-all hover:shadow-lg md:col-span-2">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <i className="fas fa-image text-white text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-black text-gray-900 text-lg mb-1">ملخص مرئي</h4>
                          <p className="text-gray-600 text-sm">صورة شاملة تلخص المحتوى بشكل مرئي</p>
                        </div>
                        <i className="fas fa-check-circle text-green-500 text-xl"></i>
                      </div>
                    </div>
                  </div>

                  {/* Info Note */}
                  <div className="mt-6 bg-white/50 rounded-xl p-4 border border-primary/10">
                    <div className="flex items-start gap-3">
                      <i className="fas fa-info-circle text-primary text-lg mt-0.5"></i>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <span className="font-bold text-primary">ملاحظة:</span> بعد الشراء، ستحصل على وصول كامل لجميع أنواع المحتوى المذكورة أعلاه
                      </p>
                    </div>
                  </div>
                </div>

                {/* Course Preview Section */}
                <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden mb-8">
                  {/* Header Section */}
                  <div className="bg-gradient-to-r from-primary to-primary-dark text-white p-6 md:p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                        <i className={`${course.category_icon} text-3xl`}></i>
                      </div>
                      <div>
                        <h2 className="text-2xl md:text-3xl font-black">معاينة محتوى الدورة</h2>
                        <p className="text-white/90 text-sm md:text-base">استكشف ما ستتعلمه في هذه الدورة</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 md:p-8 space-y-8">
                    {/* Course Description */}
                    {course.id === 9 ? (
                      <>
                        {/* Introduction */}
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <i className="fas fa-info-circle text-primary text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">نظرة عامة</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-lg">
                            راك دروك في رحلة تعلم اللغة الإيطالية! هذا الكتاب مصمم خصيصاً للجزائريين اللي حابين يتعلمو الإيطالية بطريقة سهلة وعملية. راح نستعملو الدارجة الجزائرية باش نفهمو الإيطالية مزيان. الإيطالية لغة جميلة وموسيقية - لغة الفن، الموضة، والطعام!
                          </p>
                        </div>

                        {/* Weekly Plan */}
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-calendar-week text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">📅 7 أيام تعلم مكثف</h3>
                          </div>
                          <div className="space-y-3">
                            {[
                              { day: 'اليوم 1', title: 'Foundation Basics', desc: 'الحروف الإيطالية، النطق، التحيات، Essere و Avere', icon: '🇮🇹' },
                              { day: 'اليوم 2', title: 'Daily Life Essentials', desc: 'الوقت، الأيام، الشهور، الطقس، الطعام، الروتين', icon: '☀️' },
                              { day: 'اليوم 3', title: 'People & Relationships', desc: 'العائلة، المهن، المشاعر، الصفات، الملكية', icon: '👨‍👩‍👧' },
                              { day: 'اليوم 4', title: 'Places & Directions', desc: 'الأماكن، الاتجاهات، وسائل النقل، حروف الجر', icon: '🗺️' },
                              { day: 'اليوم 5', title: 'Shopping & Money', desc: 'التسوق، المال، الملابس، الألوان، الأرقام الكبيرة', icon: '🛍️' },
                              { day: 'اليوم 6', title: 'Communication', desc: 'المكالمات، الإنترنت، التواصل الاجتماعي، Tu vs Lei', icon: '📱' },
                              { day: 'اليوم 7', title: 'Culture & Advanced', desc: 'الثقافة الإيطالية، التعابير، الآداب، المطاعم', icon: '🍝' }
                            ].map((item, i) => (
                              <div key={i} className="flex items-center gap-4 bg-white rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="text-3xl">{item.icon}</div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-black text-green-600 text-lg">{item.day}:</span>
                                    <span className="font-bold text-gray-900">{item.title}</span>
                                  </div>
                                  <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* What You'll Learn */}
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-graduation-cap text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">🎯 واش راح تتعلم</h3>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3">
                            {[
                              'الأبجدية الإيطالية (21 حرف)',
                              'النطق الخاص: gli, gn, sc, ch',
                              'التحيات: Ciao, Buongiorno, Grazie',
                              'الأفعال المهمة: Essere و Avere',
                              'الأرقام من 1 إلى 100',
                              'الوقت والأيام والشهور',
                              'الطعام والمشروبات الإيطالية',
                              'العائلة والمهن والصفات',
                              'الأماكن والاتجاهات',
                              'التسوق والأسعار',
                              'المحادثات اليومية',
                              'الثقافة والتعابير الإيطالية'
                            ].map((item, i) => (
                              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                <span className="text-blue-500 text-xl flex-shrink-0">✅</span>
                                <p className="text-gray-800 font-semibold">{item}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Why Italian */}
                        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-heart text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">❤️ علاش الإيطالية؟</h3>
                          </div>
                          <div className="space-y-3">
                            {[
                              { icon: '🎨', title: 'لغة الفن والثقافة', desc: 'إيطاليا بلاد الفن، الموسيقى، والتاريخ العريق' },
                              { icon: '🍕', title: 'لغة الطعام', desc: 'أفضل مطبخ في العالم - Pizza, Pasta, Gelato!' },
                              { icon: '👔', title: 'لغة الموضة', desc: 'Milano عاصمة الموضة العالمية - Armani, Versace, Gucci' },
                              { icon: '🌍', title: 'سهلة التعلم', desc: 'لغة موسيقية وسهلة للعرب - كل حرف ينطق!' }
                            ].map((item, i) => (
                              <div key={i} className="flex items-start gap-4 bg-white rounded-lg p-4">
                                <div className="text-3xl flex-shrink-0">{item.icon}</div>
                                <div>
                                  <h4 className="font-black text-gray-900 mb-1">{item.title}</h4>
                                  <p className="text-gray-700 text-sm">{item.desc}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Key Features */}
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <i className="fas fa-star text-purple-600 text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">مميزات الكتاب</h3>
                          </div>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200">
                              <div className="w-10 h-10 mb-2 bg-orange-500 rounded-lg flex items-center justify-center">
                                <i className="fas fa-book text-white text-lg"></i>
                              </div>
                              <h4 className="font-black text-gray-900 mb-1">100 صفحة</h4>
                              <p className="text-sm text-gray-600">محتوى شامل ومنظم بعناية</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
                              <div className="text-3xl mb-2">🇩🇿</div>
                              <h4 className="font-black text-gray-900 mb-1">بالدارجة</h4>
                              <p className="text-sm text-gray-600">شروحات بالدارجة الجزائرية</p>
                            </div>
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200">
                              <div className="text-3xl mb-2">🎯</div>
                              <h4 className="font-black text-gray-900 mb-1">عملي</h4>
                              <p className="text-sm text-gray-600">أمثلة من الحياة اليومية</p>
                            </div>
                          </div>
                        </div>

                        {/* Important Notes */}
                        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-lightbulb text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">💡 نصائح النجاح</h3>
                          </div>
                          <div className="space-y-2">
                            {[
                              'خصص وقت كل يوم للتعلم (ساعة على الأقل)',
                              'مرّن الكلمات اللي تعلمتها بصوت عالي',
                              'استعمل الإيطالية في حياتك اليومية',
                              'ماتخافش من الأخطاء - كلنا نتعلمو من الأخطاء',
                              'كرّر الدروس اللي فاتت باش ماتنساهمش',
                              'استمع للموسيقى والأفلام الإيطالية'
                            ].map((tip, i) => (
                              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                <span className="text-yellow-600 flex-shrink-0">✅</span>
                                <p className="text-gray-700">{tip}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Italian Basics Preview */}
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-book-open text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">📖 معاينة المحتوى</h3>
                          </div>
                          <div className="space-y-4">
                            <div className="bg-white rounded-lg p-4">
                              <h4 className="font-black text-indigo-600 mb-2">التحيات الأساسية</h4>
                              <div className="space-y-1 text-sm">
                                <p><span className="font-bold">Ciao</span> (تشاو) - سلام</p>
                                <p><span className="font-bold">Buongiorno</span> (بونجورنو) - صباح الخير</p>
                                <p><span className="font-bold">Grazie</span> (قراتسيه) - شكراً</p>
                                <p><span className="font-bold">Per favore</span> (بير فافوري) - من فضلك</p>
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-4">
                              <h4 className="font-black text-indigo-600 mb-2">الأفعال المهمة</h4>
                              <div className="space-y-1 text-sm">
                                <p><span className="font-bold">Essere</span> - يكون (Io sono, Tu sei, Lui è)</p>
                                <p><span className="font-bold">Avere</span> - يملك (Io ho, Tu hai, Lei ha)</p>
                                <p><span className="font-bold">Andare</span> - يذهب</p>
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-4">
                              <h4 className="font-black text-indigo-600 mb-2">معلومة خاصة</h4>
                              <p className="text-gray-700 text-sm">
                                الإيطالية موسيقية بزاف! كل حرف ينطق، وماكانش حروف صامتة (إلا H). الحروف المضاعفة مهمة: <span className="font-bold">anno</span> (عام) مختلفة على <span className="font-bold">ano</span>!
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Target Audience */}
                        <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-6 border-2 border-primary/20">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="text-3xl">🎯</div>
                            <h3 className="text-2xl font-black text-primary">لمن هذا الكتاب؟</h3>
                          </div>
                          <div className="space-y-2">
                            {[
                              'المبتدئين اللي حابين يتعلمو الإيطالية من الصفر',
                              'المسافرين لإيطاليا للسياحة أو الدراسة',
                              'عشاق الثقافة والطعام الإيطالي',
                              'الطلبة اللي يحضرو للامتحانات',
                              'كل واحد حاب يتعلم لغة جميلة وسهلة'
                            ].map((item, i) => (
                              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                <span className="text-primary flex-shrink-0">✓</span>
                                <p className="text-gray-800">{item}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : course.id === 7 ? (
                      <>
                        {/* Introduction */}
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <i className="fas fa-info-circle text-primary text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">نظرة عامة</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-lg">
                            مرحبا بيك في برنامج الأسبوع! هاذا الكتاب راح يعلمك الإنجليزية في 7 أيام بالدارجة الجزائرية! كل يوم راح تتعلم حاجات جديدة بش تقدر تهدر الإنجليزية بسهولة.
                          </p>
                        </div>

                        {/* Weekly Plan */}
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-calendar-week text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">📅 خطة الأسبوع</h3>
                          </div>
                          <div className="space-y-3">
                            {[
                              { day: 'اليوم 1', title: 'الأساسيات', desc: 'الحروف والتحية', icon: '📖' },
                              { day: 'اليوم 2', title: 'الحياة اليومية', desc: 'الأرقام والوقت', icon: '⏰' },
                              { day: 'اليوم 3', title: 'الناس والعلاقات', desc: 'العائلة والأصدقاء', icon: '👨‍👩‍👧' },
                              { day: 'اليوم 4', title: 'الأماكن والإتجاهات', desc: 'المدينة والسفر', icon: '🗺️' },
                              { day: 'اليوم 5', title: 'الأكل والتسوق', desc: 'الماكلة والفلوس', icon: '🍔' },
                              { day: 'اليوم 6', title: 'الأفعال والأنشطة', desc: 'الرياضة والهوايات', icon: '⚽' },
                              { day: 'اليوم 7', title: 'التواصل الكامل', desc: 'المحادثات الحقيقية', icon: '💬' }
                            ].map((item, i) => (
                              <div key={i} className="flex items-center gap-4 bg-white rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="text-3xl">{item.icon}</div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-black text-primary text-lg">{item.day}:</span>
                                    <span className="font-bold text-gray-900">{item.title}</span>
                                  </div>
                                  <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 bg-white/50 rounded-lg p-3 border border-blue-200">
                            <p className="text-gray-700 text-sm">
                              <span className="font-bold">💡 ملاحظة:</span> كل يوم فيه 3 حصص - الصباح للكلمات الجديدة، القايلة للمحادثات، والعشية للقراءة والفهم
                            </p>
                          </div>
                        </div>

                        {/* What You'll Learn */}
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-graduation-cap text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">🎯 واش راح تتعلم</h3>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3">
                            {[
                              'الحروف الإنجليزية الـ 26',
                              'أكثر من 500 كلمة أساسية',
                              '100+ فعل شائع في الحياة اليومية',
                              'المضارع البسيط والمستمر',
                              'الماضي البسيط والمستقبل',
                              'محادثات حقيقية (50+ حوار)',
                              'التحية والتعارف بالإنجليزية',
                              'الأرقام والوقت والتاريخ',
                              'الحديث عن العائلة والأصدقاء',
                              'السؤال عن الطريق والإتجاهات',
                              'التسوق وطلب الطعام',
                              'تحديد المواعيد والخطط'
                            ].map((item, i) => (
                              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                <span className="text-green-500 text-xl flex-shrink-0">✅</span>
                                <p className="text-gray-800 font-semibold">{item}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Important Tips */}
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-lightbulb text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">💡 نصائح مهمة</h3>
                          </div>
                          <div className="space-y-2">
                            {[
                              'خصص 2-3 ساعات كل يوم للدراسة',
                              'راجع الكلمات القديمة قبل ما تبدا اليوم الجديد',
                              'حاول تهدر بالإنجليزية مع روحك أو مع أصدقائك',
                              'استعمل الإنجليزية في حياتك اليومية',
                              'شوف أفلام ومسلسلات بالإنجليزية',
                              'الممارسة اليومية أهم من الدراسة الطويلة'
                            ].map((tip, i) => (
                              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                <span className="text-orange-500 flex-shrink-0">•</span>
                                <p className="text-gray-700">{tip}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Course Features */}
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <i className="fas fa-star text-purple-600 text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">مميزات البرنامج</h3>
                          </div>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border-2 border-red-200">
                              <div className="text-3xl mb-2">🎯</div>
                              <h4 className="font-black text-gray-900 mb-1">مكثف وسريع</h4>
                              <p className="text-sm text-gray-600">تعلم أساسيات الإنجليزية في 7 أيام فقط</p>
                            </div>
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200">
                              <div className="text-3xl mb-2">🇩🇿</div>
                              <h4 className="font-black text-gray-900 mb-1">بالدارجة الجزائرية</h4>
                              <p className="text-sm text-gray-600">شروحات واضحة بلغتك الأم</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
                              <div className="text-3xl mb-2">💬</div>
                              <h4 className="font-black text-gray-900 mb-1">محادثات واقعية</h4>
                              <p className="text-sm text-gray-600">أمثلة من الحياة اليومية</p>
                            </div>
                            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border-2 border-yellow-200">
                              <div className="text-3xl mb-2">📖</div>
                              <h4 className="font-black text-gray-900 mb-1">100 صفحة</h4>
                              <p className="text-sm text-gray-600">محتوى شامل ومنظم</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200">
                              <div className="text-3xl mb-2">✍️</div>
                              <h4 className="font-black text-gray-900 mb-1">تمارين يومية</h4>
                              <p className="text-sm text-gray-600">واجبات لتثبيت المعلومات</p>
                            </div>
                            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-4 border-2 border-pink-200">
                              <div className="text-3xl mb-2">🎓</div>
                              <h4 className="font-black text-gray-900 mb-1">من الصفر</h4>
                              <p className="text-sm text-gray-600">لا تحتاج معرفة مسبقة</p>
                            </div>
                          </div>
                        </div>

                        {/* Daily Structure */}
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-clock text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">⏰ هيكل اليوم الدراسي</h3>
                          </div>
                          <div className="space-y-3">
                            <div className="bg-white rounded-lg p-4">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-2xl">🌅</span>
                                <h4 className="font-black text-gray-900">حصة الصباح</h4>
                              </div>
                              <p className="text-gray-700">تعلم الكلمات والمفردات الجديدة</p>
                            </div>
                            <div className="bg-white rounded-lg p-4">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-2xl">☀️</span>
                                <h4 className="font-black text-gray-900">حصة القايلة</h4>
                              </div>
                              <p className="text-gray-700">ممارسة المحادثات والحوارات</p>
                            </div>
                            <div className="bg-white rounded-lg p-4">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-2xl">🌙</span>
                                <h4 className="font-black text-gray-900">حصة العشية</h4>
                              </div>
                              <p className="text-gray-700">القراءة والفهم والمراجعة</p>
                            </div>
                          </div>
                        </div>

                        {/* Target Audience */}
                        <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-6 border-2 border-primary/20">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="text-3xl">🎯</div>
                            <h3 className="text-2xl font-black text-primary">لمن هذا البرنامج؟</h3>
                          </div>
                          <div className="space-y-2">
                            {[
                              'المبتدئين اللي حابين يتعلمو الإنجليزية من الصفر',
                              'الطلبة اللي يحضرو لامتحانات اللغة',
                              'الموظفين اللي يحتاجو الإنجليزية في عملهم',
                              'المسافرين اللي يبغاو يهدرو مع الناس برا',
                              'كل واحد حاب يتعلم الإنجليزية بطريقة سريعة وفعالة'
                            ].map((item, i) => (
                              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                <span className="text-primary flex-shrink-0">✓</span>
                                <p className="text-gray-800">{item}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : course.id === 12 ? (
                      <>
                        {/* Introduction */}
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <i className="fas fa-info-circle text-primary text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">نظرة عامة</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-lg">
                            مرحباً بكم في كتاب "علم ابني الأرقام"! هذا الكتاب مصمم خصيصاً للأطفال من سن 3 إلى 5 سنوات لتعليمهم الأرقام بطريقة ممتعة وسهلة باستخدام الدارجة الجزائرية.
                          </p>
                        </div>

                        {/* What's Inside */}
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-book-open text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">واش في الكتاب؟</h3>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3">
                            <div className="flex items-start gap-3 bg-white rounded-lg p-3">
                              <span className="text-green-500 text-xl flex-shrink-0">✅</span>
                              <p className="text-gray-800 font-semibold">الأرقام من 0 إلى 20 بالتفصيل</p>
                            </div>
                            <div className="flex items-start gap-3 bg-white rounded-lg p-3">
                              <span className="text-green-500 text-xl flex-shrink-0">✅</span>
                              <p className="text-gray-800 font-semibold">أمثلة من الحياة اليومية</p>
                            </div>
                            <div className="flex items-start gap-3 bg-white rounded-lg p-3">
                              <span className="text-green-500 text-xl flex-shrink-0">✅</span>
                              <p className="text-gray-800 font-semibold">رسومات وإيموجي ملونة</p>
                            </div>
                            <div className="flex items-start gap-3 bg-white rounded-lg p-3">
                              <span className="text-green-500 text-xl flex-shrink-0">✅</span>
                              <p className="text-gray-800 font-semibold">مفاهيم الجمع والطرح البسيط</p>
                            </div>
                            <div className="flex items-start gap-3 bg-white rounded-lg p-3">
                              <span className="text-green-500 text-xl flex-shrink-0">✅</span>
                              <p className="text-gray-800 font-semibold">مقارنة الأرقام (أكبر/أصغر)</p>
                            </div>
                            <div className="flex items-start gap-3 bg-white rounded-lg p-3">
                              <span className="text-green-500 text-xl flex-shrink-0">✅</span>
                              <p className="text-gray-800 font-semibold">100 صفحة من المرح والتعلم!</p>
                            </div>
                          </div>
                        </div>

                        {/* Tips for Parents */}
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-lightbulb text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">💡 نصائح للاستفادة القصوى</h3>
                          </div>
                          <div className="space-y-2">
                            {[
                              'اقرأ مع طفلك كل يوم 10-15 دقيقة',
                              'استخدم أصابع اليد لعد الأرقام',
                              'ابحث عن الأمثلة في المنزل (تفاحات، كراسي، ألعاب)',
                              'كرر الأرقام بالدارجة والعربية الفصحى',
                              'اجعل التعلم ممتعاً ومليء بالمرح!',
                              'لا تضغط على الطفل، كل طفل يتعلم بسرعته'
                            ].map((tip, i) => (
                              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                <span className="text-purple-500 flex-shrink-0">🔸</span>
                                <p className="text-gray-700">{tip}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* How to Use */}
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                              <i className="fas fa-rocket text-white text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">🌟 كيفاه نستعمل الكتاب</h3>
                          </div>
                          <ol className="space-y-3">
                            {[
                              'ابدأ بالأرقام الصغيرة (0-5) وخذ وقتك',
                              'اشر للرقم وقول الاسم بالدارجة',
                              'عد الإيموجي مع طفلك بصوت عالي',
                              'جرب الأمثلة في البيت',
                              'راجع الأرقام القديمة قبل الجديدة',
                              'احتفل بكل تقدم يقوم به طفلك! 🎉'
                            ].map((step, i) => (
                              <li key={i} className="flex items-start gap-3 bg-white rounded-lg p-3">
                                <span className="text-orange-500 font-black text-lg flex-shrink-0">{i + 1}.</span>
                                <p className="text-gray-700 font-medium">{step}</p>
                              </li>
                            ))}
                          </ol>
                        </div>

                        {/* Course Structure */}
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <i className="fas fa-list text-green-600 text-xl"></i>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">محتويات الدورة</h3>
                          </div>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200">
                              <div className="text-3xl mb-2">📊</div>
                              <h4 className="font-black text-gray-900 mb-1">الأرقام 0-10</h4>
                              <p className="text-sm text-gray-600">تعلم الأرقام الأساسية مع أمثلة وإيموجي</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200">
                              <div className="text-3xl mb-2">📈</div>
                              <h4 className="font-black text-gray-900 mb-1">الأرقام 11-20</h4>
                              <p className="text-sm text-gray-600">توسيع المعرفة للأرقام الكبيرة</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
                              <div className="text-3xl mb-2">➕</div>
                              <h4 className="font-black text-gray-900 mb-1">الجمع والطرح</h4>
                              <p className="text-sm text-gray-600">مبادئ الحساب البسيط</p>
                            </div>
                            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200">
                              <div className="text-3xl mb-2">⚖️</div>
                              <h4 className="font-black text-gray-900 mb-1">المقارنة</h4>
                              <p className="text-sm text-gray-600">أكبر من، أصغر من، يساوي</p>
                            </div>
                            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border-2 border-red-200">
                              <div className="text-3xl mb-2">🎮</div>
                              <h4 className="font-black text-gray-900 mb-1">الألعاب والتطبيقات</h4>
                              <p className="text-sm text-gray-600">أنشطة تفاعلية ممتعة</p>
                            </div>
                            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border-2 border-yellow-200">
                              <div className="text-3xl mb-2">🏆</div>
                              <h4 className="font-black text-gray-900 mb-1">شهادة الإتمام</h4>
                              <p className="text-sm text-gray-600">شهادة باسم الطفل</p>
                            </div>
                          </div>
                        </div>

                        {/* Target Audience */}
                        <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-6 border-2 border-primary/20">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="text-3xl">👶</div>
                            <h3 className="text-2xl font-black text-primary">الفئة المستهدفة</h3>
                          </div>
                          <p className="text-gray-800 leading-relaxed text-lg">
                            <span className="font-black text-primary">للأطفال من 3 إلى 5 سنوات:</span> هذا الكتاب مصمم خصيصاً للمرحلة العمرية المبكرة حيث يبدأ الأطفال في فهم المفاهيم العددية. التعلم بالدارجة الجزائرية يجعل الطفل يستوعب الأرقام بشكل طبيعي ويربطها بحياته اليومية.
                          </p>
                        </div>
                      </>
                    ) : (
                      /* Default template for other courses */
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <i className="fas fa-info-circle text-primary text-xl"></i>
                          </div>
                          <h3 className="text-2xl font-black text-gray-900">عن الدورة</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                          {course.description_ar}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {isPurchased ? (
                  <Link
                    to={`/course/${course.id}/view`}
                    className="w-full bg-primary text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-primary-dark transition-all duration-300 shadow-lg flex items-center justify-center gap-3"
                  >
                    <FaCheckCircle />
                    ابدأ القراءة
                  </Link>
                ) : (
                  <button
                    onClick={() => setShowPurchaseModal(true)}
                    className="w-full bg-primary text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-primary-dark transition-all duration-300 shadow-lg flex items-center justify-center gap-3"
                  >
                    <FaShoppingCart />
                    اشتري الآن - {course.price} دج
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Purchase Bar */}
      {!isPurchased && showStickyBar && (
        <div className="fixed top-0 left-0 right-0 bg-white shadow-lg z-[9999] border-b-2 border-primary/20 animate-slide-down">
          <div className="container mx-auto px-4 py-3">
            <div className="max-w-[1200px] mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1">
                {course.summary_image_url && (
                  <img 
                    src={`${API_BASE_URL}/api/courses/${course.id}/thumbnail`}
                    alt={course.title_ar}
                    className="w-12 h-12 object-cover rounded-lg hidden md:block"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-black text-gray-900 text-base md:text-lg line-clamp-1">
                    {course.title_ar}
                  </h3>
                  <p className="text-primary font-bold text-sm md:text-base">
                    {course.price} دج
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowPurchaseModal(true)}
                className="bg-primary text-white px-6 py-3 rounded-xl font-bold text-sm md:text-base hover:bg-primary-dark transition-all duration-300 shadow-lg flex items-center gap-2 whitespace-nowrap"
              >
                <FaShoppingCart />
                اشتري الآن
              </button>
            </div>
          </div>
        </div>
      )}

      {showPurchaseModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPurchaseModal(false)}
        >
          <div 
            className="bg-white rounded-3xl p-8 max-w-[500px] w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* X Close Button */}
            <button
              onClick={() => setShowPurchaseModal(false)}
              className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 text-2xl w-8 h-8 flex items-center justify-center transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>

            <h2 className="text-3xl font-black text-gray-800 mb-6">تأكيد الشراء</h2>
            
            <div className="bg-bg p-6 rounded-2xl mb-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-gray-600">الكتاب:</span>
                <span className="font-bold text-gray-800">{course.title_ar}</span>
              </div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-gray-600">السعر:</span>
                <span className="font-bold text-primary text-2xl">{course.price} دج</span>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-bold">المجموع:</span>
                  <span className="font-black text-primary text-3xl">{course.price} دج</span>
                </div>
              </div>
            </div>

            {/* Online Payment Button */}
            <div className="mb-6">
              <button
                onClick={handlePurchase}
                disabled={purchasing}
                className="w-full bg-primary text-white px-6 py-4 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <i className="fas fa-credit-card"></i>
                {purchasing ? 'جاري التحويل للدفع...' : 'دفع أونلاين'}
              </button>
              <p className="text-xs text-gray-600 text-center mt-2">
                ملاحظة: سيتم تحويلك لصفحة الدفع الآمنة (CIB / EDAHABIA)
              </p>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-text3">أو</span>
              </div>
            </div>

            {/* Manual CCP Payment Button */}
            <div>
              <button
                onClick={() => {
                  setShowPurchaseModal(false);
                  navigate(`/manual-payment/${course.id}`);
                }}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-xl font-bold hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <i className="fas fa-money-bill-wave"></i>
                الدفع اليدوي عبر CCP (حوالة بريدية)
              </button>
              <p className="text-xs text-gray-600 text-center mt-2">
                للدفع عبر الحساب الجاري البريدي
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseDetailPage;
